import React from 'react';
import PropTypes from 'prop-types';
import graphql from 'babel-plugin-relay/macro';
import {createFragmentContainer} from 'react-relay';

import withWidth from '@material-ui/core/withWidth';
import withStyles from '@material-ui/core/styles/withStyles';


import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import Collapse from '@material-ui/core/Collapse';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Icon from '../Icon';

import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';

import NavListWithCollapse from './NavListWithCollapse';

import Typography from '../Typography';
import SearchField from '../SearchField';

//import MiddleSectionOrgs from './NavContentOrgs';
import Header from './NavHeader';

import BottomSection from './BottomSection';

const iOS = process.browser && /iPad|iPhone|iPod/.test(navigator.userAgent);

const drawer_width = 300;

/*///////////////////////////////////////////////////////////////////////////////////
                                MIDDLE SECTION
///////////////////////////////////////////////////////////////////////////////////*/

const middle_section_styles = theme => ({
  app_changer_class: {
    marginTop: 'auto',
    cursor: 'pointer',
    marginBottom:0,
  },
  list_item: {
    padding:theme.margins.sm
  },
  zero_margin_right: {
    marginRight:0,
    minWidth: 36
  },
  overflow: {
    textOverflow: 'ellipsis',
    overflow:'hidden',
    whiteSpace: 'nowrap'
  }
});

const MiddleSection = React.memo(withStyles(middle_section_styles)(({
  classes,
  app_changer,
  ...props
}) => (
  <>
    <NavListWithCollapse {...props} />
    {
      Boolean(app_changer && app_changer.length) ?
      (
        <List className = {classes.app_changer_class}>
        {
          app_changer.map(({ icon_type, name, link },index) => (
            <React.Fragment key = {`app-changer-${name}-${index}`}>
            <Divider />
            <ListItem classes = {{ root: classes.list_item}}>
              <ListItemIcon className = {classes.zero_margin_right}>
                <Icon size = "sm" type = {icon_type} />
              </ListItemIcon>
              <ListItemText
                  primary = {name}
                  component = { props.link_component }
                  href = {link}
                  classes = {{
                    primary: classes.overflow
                  }}
              />
              </ListItem>
            </React.Fragment>
          ))
        }
        </List>
      ) : null
    }
  </>
)))

/*///////////////////////////////////////////////////////////////////////////////////
                                  MIDDLE HEADER SECTION
///////////////////////////////////////////////////////////////////////////////////*/

const middle_header_section_styles = theme => ({
  root: {
    flex: 1,
    flexDirection: 'column'
  },
  list_item: {
    padding: theme.margins.sm,
    '&:hover': {
      background: theme.palette.lightGrey,
    },
  },
  selected: {
    fontWeight: 600
  },
  list_item_text: {
    color: `${theme.palette.darkBlack} !important`,
    fontWeight: `${theme.fontWeight.main} !important`,
    cursor:'pointer',
    textOverflow: 'ellipsis',
    overflow:'hidden',
    whiteSpace: 'nowrap',
  }
})


const MiddleHeaderSection = React.memo(withStyles(middle_header_section_styles)(({
  classes,
  logout_function,
  header_props,
  pathname,
  ...props
}) => {

  const check_if_selected = (item, path) => new RegExp(path, "g").test(item);
  const create_path = (path = '/') =>  Boolean(path) && /^\//.test(path) ? path : Boolean(path) ? '/' + path : '/';


  return (
    <List className = {classes.root}>
       {
         Boolean(header_props && header_props.length)
         ? header_props.map(({
           to, // ** where does this item link to **
           path, // ** when to show this item as selected (bold) **
           name,
           disabled,
           link_component,
           ...other
         }, index) => {

           const data_selected = check_if_selected(path, pathname);

           return (
             <ListItem
               key= {name}
               selected = { data_selected }
               component = { link_component || props.link_component }
               to = { create_path(to) }
               disabled = {disabled}
               onClick = { props.menu_open ? null : props.onClose }
               classes = {{ root: classes.list_item }}
               {...other}
              >
               <ListItemText
                 primary= {name}
                 classes = {{
                   primary: `${classes.list_item_text} ${data_selected ? classes.selected : ''}`,
                 }}
               />
             </ListItem>
           );
         }
       ): null
       }
       <ListItem
         key= 'signout'
         id = "signout"
         onClick = { logout_function }
         classes = {{ root: classes.list_item }}
        >
         <ListItemText
           primary= {'Sign Out'}
           classes = {{
             primary: classes.list_item_text,
           }}
         />
       </ListItem>
   </List>
  )
}))



/*///////////////////////////////////////////////////////////////////////////////////
                                  NAV CONTENT
///////////////////////////////////////////////////////////////////////////////////*/

const styles = theme => ({
  root: {
      width: drawer_width,
      background: theme.palette.whiteGrey,
      flexWrap:'nowrap',
      flex: 1,
      [theme.breakpoints.up('lg')]: {
        width: '100%',
        maxWidth: drawer_width,
      }
  },
  container: {
    flex: 1,
    overflowY:'auto'
  },
  container_selected_org: {
    marginBottom: 64,
  },
  container_selected_property: {
    marginBottom: 88,
  }
})

const NavContent = ({
  classes: {
    root,
    container,
    container_selected_property,
    container_selected_org
  },
  user: {
    name,
  },
  orgs,
  app_changer,
  header_props,
  logout_function,
  pathname,
  suborg_change_available,
  ...props
}) => {


    const [page, set_page] = React.useState("default");

    function header_changer(e) { set_page(page === "head" ? "default" : "head"); }

    const container_class = React.useMemo(() => {
      return Boolean(props.orgs_state.selected_property && props.orgs_state.selected_property.name)
        ? container_selected_property
        : container_selected_org
    },[props.orgs_state.selected_property])

    return (
      <Grid
          container
          direction = "column"
          className = {root}
        >
          <Grid
            direction = "column"
            container
            wrap="nowrap"
            className = {container + ' ' + container_class}
          >
          {
            /default|head/.test(page)
            ? <Header
                name = { name }
                onClick = {header_changer}
                pressed = {page === "head"}
              />
            : null
          }
          { page === "head" ? <MiddleHeaderSection logout_function = {logout_function} pathname = {pathname} header_props = {header_props} /> : null }
          { page === "default" ? <MiddleSection app_changer = {app_changer} pathname = {pathname} {...props} /> : null }
          { page !== "head" ? <BottomSection suborg_change_available = {Boolean(suborg_change_available)} {...props}/> : null }
        </Grid>
      </Grid>
   );
}
// { /properties|orgs/.test(page) ? <MiddleSectionOrgs  page = {page} pathname = {pathname} orgs = {orgs} {...props}/> : null }

const isValidComponent = (props, propName, name) => {
    if (props[propName] && !React.isValidElement(props[propName]) && !(typeof props[propName] === "string")) {
      return new Error(
        //`Invalid prop 'component' supplied to '${name}': the prop is not a valid React component`
        `Expect prop '${name}' to be either a string or a valid react component. Got ${typeof props[propName]}.`
      );
    }
}

const one_of_props = (props, propName, componentName) => {
    if (!props.to && !props.href) {
      return new Error(`One of props 'to' or 'href' was not specified in '${componentName}'.`);
    }
};

NavContent.propTypes = {

  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    container: PropTypes.string.isRequired
  }),

  user: PropTypes.shape({
     name: PropTypes.string,
     property:PropTypes.string,
    // org: PropTypes.shape({
      // id: PropTypes.string,
     //})
  }),

  orgs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ),

  bottom_section: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

  onClose: PropTypes.func.isRequired,

  logout_function: PropTypes.func,

  app_changer: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    icon_type: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired
  })),

  header_props: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    to: one_of_props,
    href: one_of_props,
    disabled: PropTypes.bool,
    link_component: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
  })),

  suborg_change_available: PropTypes.bool,

  pathname: PropTypes.string.isRequired,

  onSelect: PropTypes.func

}

NavContent.defaultProps = {

  link_component: 'div',

  is_online: true,

  menu_open: false,

  suborg_change_available: false,

  pathname: '/',

}

export default withStyles(styles)(NavContent);
